import { ReactElement } from 'react';
import { RequirePermission } from '@backstage/plugin-permission-react';
import {
  attachComponentData,
  getComponentData,
} from '@backstage/core-plugin-api';
import type {
  Permission,
  ResourcePermission,
} from '@backstage/plugin-permission-common';

/**
 * Wraps a routable page element with {@link RequirePermission} while preserving
 * the routable component data (`core.mountPoint` / `core.plugin`) on the
 * resulting wrapper component.
 *
 * Background: The New Frontend System compat layer (`collectLegacyRoutes`) reads
 * the mount point only from the *top-level* `element` of each `<Route>` and does
 * NOT descend into the element's React children. Wrapping a routable page with
 * `<RequirePermission>` therefore hides its `routeRef`, leading to the runtime
 * error:
 *   "Routable extension component with mount point routeRef{...} was not
 *    discovered in the app element tree."
 *
 * By forwarding the page's component data onto the wrapper, the converter can
 * still discover the route ref while keeping the permission gating intact.
 *
 * // temporary solution for Legacy routes
 * <Route
 *   path="/admin"
 *   element={withRoutablePermission(<AdminPage />, adminPermission)}
 * />
 *
 * // after migration to new frontend system
 * export const AdminPageExtension = createPageExtension({
 *   defaultPath: '/admin',
 *   routeRef: adminRouteRef,
 *   permission: { permission: adminPermission, onForbidden: 'hide' },
 *   loader: () => import('./AdminPage'),
 * });
 *
 * TODO This method can be removed after all legacy routes have been migrated to the new frontend system and the compat layer is no longer needed.
 */
export function withRoutablePermission(
  page: ReactElement,
  permission: Exclude<Permission, ResourcePermission>,
): ReactElement {
  return withRoutableWrapper(page, routablePage => (
    <RequirePermission permission={permission}>{routablePage}</RequirePermission>
  ));
}

export function withRoutableWrapper(
  page: ReactElement,
  wrapPage: (page: ReactElement) => ReactElement,
): ReactElement {
  const Wrapped = () => wrapPage(page);

  const mountPoint = getComponentData(page, 'core.mountPoint');
  const plugin = getComponentData(page, 'core.plugin');

  if (mountPoint) {
    attachComponentData(Wrapped, 'core.mountPoint', mountPoint);
  }
  if (plugin) {
    attachComponentData(Wrapped, 'core.plugin', plugin);
  }

  return <Wrapped />;
}
